import { IClientResponse } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/client';
import axios from 'axios';
import { injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { HoopsAiArticle, HoopsAiPortfolio, NewsArticle, PassportAvailableLanguages } from '../types/News.type';
import { formatOrbisNews, formatPassportNews, parseCryptoSymbol } from '../utils';
import { INewsApi } from './interfaces/INewsApi';

@injectable()
export class NewsApi extends BaseApi implements INewsApi {
    async getFeaturedPassportItems(language?: PassportAvailableLanguages): Promise<NewsArticle[]> {
        const featuredIds = (await axios.get('https://www.pws.io/api/slider_articles')).data.slider_articles.slice(
            0,
            3,
        );
        const responses: IClientResponse[] = await Promise.all(
            featuredIds.map((item: any) =>
                this.sdks.middleware.post('insight/passport/article-by-id', {
                    ID: item,
                    language: language ?? 'eng',
                }),
            ),
        );

        return responses.map((item) => formatPassportNews(item.data));
    }

    async getPassportNews(params?: {
        symbol?: string;
        limit?: number;
        tag?: string;
        tagGroup?: string;
        releasedAfter?: number;
        withNewsFeed?: boolean;
        language?: PassportAvailableLanguages
    }): Promise<NewsArticle[]> {
        const { symbol, limit, tag, tagGroup, releasedAfter, withNewsFeed, language } = params || {};
        const response = await this.sdks.middleware.post('insight/passport/articles', {
            symbol: symbol,
            count: limit,
            tag: tag,
            tag_group: tagGroup,
            released_after: releasedAfter,
            include_newsfeed: withNewsFeed,
            language: language ?? 'eng',
        });

        return response.data.map((article: NewsArticle) => formatPassportNews(article));
    }

    async getPassportNewsById(id: number, language?: PassportAvailableLanguages): Promise<NewsArticle> {
        const response = await this.sdks.middleware.post('insight/passport/article-by-id', {
            ID: id,
            language: language ?? 'eng',
        });
        return formatPassportNews(response.data);
    }

    async getOrbisNewsById(id: number): Promise<NewsArticle> {
        const response = await this.sdks.orbis.get('research/news', undefined, { urlQuery: id });
        return formatOrbisNews(response.data);
    }

    async getNewsfeed(params?: { tag?: string; tagGroup?: string; limit: number }): Promise<NewsArticle[]> {
        const { limit, tag, tagGroup } = params || {};
        const response = await this.sdks.middleware.get('passport/newsfeed/eng/all', undefined, {
            params: { with_tag: tag, with_tag_group: tagGroup, count: limit },
        });
        return response.data.map((article: NewsArticle) => formatPassportNews(article));
    }

    // TODO: We need to pass quotes here and not array of strings, so we can distinguish if the quote is crypto or not and if so, we can add quote currency. We can use the crypto boolean on Quote to distinguish this
    // We could want to call this function with an array of symbols that contatins crypto and equity symbols in the same array.
    async getHoopsAiSymbolArticle(
        symbols: string[],
        options?: { quoteCurrency?: string; techLevel?: number; length?: number },
    ): Promise<HoopsAiArticle[]> {
        const response = await Promise.all(
            symbols.map((symbol) =>
                this.sdks.middleware.get(
                    // @ts-ignore: URL not in SDK
                    `hoopsai/v1/resources/finance/daily`,
                    undefined,
                    {
                        urlQuery: `${symbol + options?.quoteCurrency ?? ''}`,
                        params: {
                            length: options?.length || 1,
                            separate_header: true,
                            enable_html: false,
                            doc_group: 'Stocks_Daily',
                            technical_level: options?.techLevel || 1,
                        },
                    },
                ),
            ),
        );
        const articles = response.map((response) => {
            return response.data.result;
        });
        return articles;
    }

    // TODO: We need to pass quotes here and not array of strings, so we can distinguish if the quote is crypto or not and if so, we can add quote currency. We can use the crypto boolean on Quote to distinguish this
    // We could want to call this function with an array of symbols that contatins crypto and equity symbols in the same array.
    async getHoopsAiPortfolioArticle(
        holdings: { symbol: string; quantity: number }[],
        options?: {
            techLevel?: number;
            length?: number;
            quoteCurrency?: string;
        },
    ): Promise<HoopsAiPortfolio> {
        const qc = options?.quoteCurrency ? options?.quoteCurrency : '';
        const formattedHoldings = holdings.map((holding) => {
            return {
                symbol: `${holding.symbol.toUpperCase() + qc}`,
                num_of_shares: holding.quantity,
            };
        });
        const response = await this.sdks.middleware.post(
            // @ts-ignore: URL not in SDK
            'hoopsai/v1/resources/finance/article/portfolio',
            { portfolio: formattedHoldings },
            {
                params: {
                    doc_group: 'Stocks_Daily',
                    technical_level: options?.techLevel || 1,
                    length: options?.length || 5,
                },
            },
        );
        return response.data.result;
    }

    // TODO: We need to pass quotes here and not array of strings, so we can distinguish if the quote is crypto or not and if so, we can add quote currency.
    // We could want to call this function with an array of symbols that contatins crypto and equity symbols in the same array. We can use the crypto flag on quote to distinguish this
    async getHoopsAiWatchlistArticle(
        symbols: string[],
        options?: {
            techLevel?: number;
            separateHeader?: boolean;
            enableHTML?: boolean;
            quoteCurrency?: string;
        },
    ): Promise<HoopsAiArticle> {
        const response = await this.sdks.middleware.get(
            // @ts-ignore: URL not in SDK
            'hoopsai/v1/resources/finance/article/watchlist/:symbols',
            undefined,
            {
                params: {
                    doc_group: 'Stocks_Daily',
                    technical_level: options?.techLevel || 1,
                    separateHeader: options?.separateHeader || false,
                    enable_html: options?.enableHTML || true,
                },
                pathParams: {
                    symbols: options?.quoteCurrency
                        ? symbols
                              .map((symbol) => parseCryptoSymbol(symbol).base + options?.quoteCurrency ?? '')
                              .join(',')
                        : symbols.join(','),
                },
            },
        );
        return response.data.result;
    }
}
